import React from 'react';
import clsx from 'clsx';
import styles from './PrintPetInfo.module.scss';
import Pet from '@spike/pet-model';
import PetImage from 'components/UI/PetCard/PetImage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarDays,
    faCat,
    faDog,
    faHeartPulse,
    faMars,
    faNarwhal,
    faRuler,
    faVenus
} from '@fortawesome/pro-light-svg-icons';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { useTimeZone } from 'hooks';
import { formatPetAgeFromBirthdate } from 'utils/PetAgeUtils';

type Props = {
    pet: Pet;
    className?: string;
};

const getBreedLabel = (pet: Pet): string => pet?.breed?.name ?? '';
const getGenderLabel = (pet: Pet): string => pet?.gender?.name ?? '';
const getWeightLabel = (pet: Pet): string => {
    const w = pet?.weight;
    const size = pet?.size?.name;
    if (!w && !size) return '';
    if (w && size) return `${w} Lbs (${size})`;
    if (w) return `${w} Lbs`;
    return `${size}`;
};

const getAgeLabel = (pet: Pet, timezone: string): string => formatPetAgeFromBirthdate(pet?.birthdate, timezone);

const getPersonalities = (pet: Pet) => {
    const positive = pet?.positivePersonalities ?? [];
    const negative = pet?.negativePersonalities ?? [];
    return [...positive, ...negative].slice().sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''));
};

export const PrintPetInfo: React.FC<Props> = ({ pet, className }) => {
    const timezone = useTimeZone();
    const personalities = getPersonalities(pet);

    return (
        <div className={clsx(styles.root, className)}>
            <div className={styles.left}>
                <div className={styles.avatar}>
                    <PetImage
                        petImage={pet?.image}
                        petType={pet?.type?.id}
                        imageClassName={styles.avatarImage}
                    />
                </div>

                <div className={styles.meta}>
                    <div className={styles.nameRow}>
                        <div className={styles.name}>{pet?.name}</div>

                        {pet?.hasMedicalConditions && (
                            <FontAwesomeIcon
                                size="sm"
                                icon={faHeartPulse}
                                className={styles.specialCareIcon}
                            />
                        )}

                        {(pet?.negativePersonalities?.length ?? 0) > 0 && (
                            <FontAwesomeIcon
                                size="sm"
                                icon={faTriangleExclamation}
                                className={styles.medicalIcon}
                            />
                        )}
                        {pet?.deceased && <span className={styles.deceasedIcon}>🌈</span>}
                    </div>

                    <div className={styles.row}>
                        <span className={styles.item}>
                            <FontAwesomeIcon
                                size="sm"
                                icon={
                                    pet?.type?.singular === 'Cat'
                                        ? faCat
                                        : pet?.type?.singular === 'Dog'
                                        ? faDog
                                        : faNarwhal
                                }
                                className={styles.icon}
                            />
                            <span className={styles.text}>{getBreedLabel(pet)}</span>
                        </span>

                        <span className={styles.item}>
                            <FontAwesomeIcon
                                size="sm"
                                icon={pet?.gender?.id === 'female' ? faVenus : faMars}
                                className={styles.icon}
                            />
                            <span className={styles.text}>{getGenderLabel(pet)}</span>
                        </span>
                    </div>

                    <div className={styles.row}>
                        <span className={styles.item}>
                            <FontAwesomeIcon
                                size="sm"
                                icon={faCalendarDays}
                                className={styles.icon}
                            />
                            <span className={styles.text}>{getAgeLabel(pet, timezone)}</span>
                        </span>

                        <span className={styles.item}>
                            <FontAwesomeIcon
                                size="sm"
                                icon={faRuler}
                                className={styles.icon}
                            />
                            <span className={styles.text}>{getWeightLabel(pet)}</span>
                        </span>
                    </div>
                </div>
            </div>

            <div className={styles.right}>
                <div className={styles.personalityTitle}>Personality</div>

                <div className={styles.chips}>
                    {personalities.map((t: any) => (
                        <span
                            key={t.id ?? t.name}
                            className={styles.chip}
                        >
                            {t.name}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default PrintPetInfo;
